import { Stack } from '@mui/material';
import {
  CreateSalesPriceConditionForm,
  CreateSalesPriceConditionSchema,
  getConditionBindingValuesFromLevelSchema,
} from '@retail/calculus/components';
import { CreateItemLevelSalesPriceConditionForm } from './CreateItemLevelSalesPriceConditionForm';
import { useController, useFormContext } from 'react-hook-form';
import { PriceSuggestionTypeWithCount } from '@retail/price-suggestion/types';
import { useGetActiveMarkupCondition } from '@retail/calculus/data-access';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useMemo } from 'react';

interface Props {
  priceSuggestionTypes: PriceSuggestionTypeWithCount[];
  includeMarkup: boolean;
  supplierNumber?: number;
  isLoadingFetchPriceSuggestions: boolean;
}

export function CreateSalesPriceConditionDialogContent({
  priceSuggestionTypes,
  includeMarkup,
  supplierNumber,
  isLoadingFetchPriceSuggestions,
}: Props) {
  const { assortmentId } = useSelectedOrgUnit();
  const { control, getValues } = useFormContext<CreateSalesPriceConditionSchema>();

  const bindingLevel = useMemo(
    () => getConditionBindingValuesFromLevelSchema({ levelSchema: getValues(), supplierNumber })[0],
    [getValues, supplierNumber]
  );

  const { data: inheritedMarkup } = useGetActiveMarkupCondition({
    assortmentId,
    bindingLevel: bindingLevel,
    enabled: !!getValues().level && getValues().level !== 'assortment',
  });

  const itemController = useController({ name: 'item', control });
  const gtinController = useController({ name: 'gtin' });

  return (
    <Stack>
      {!!itemController.field.value ||
      (!!itemController.field.value && !!gtinController.field.value) ? (
        <CreateItemLevelSalesPriceConditionForm
          mgItemNumber={itemController.field.value.mgItemNumber}
          mgSupplierId={itemController.field.value.mgSupplierId}
          includeMarkup={includeMarkup}
          markupCondition={inheritedMarkup}
          isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
        />
      ) : (
        <CreateSalesPriceConditionForm
          priceSuggestionTypes={priceSuggestionTypes}
          markupCondition={inheritedMarkup}
          includeMarkup={includeMarkup}
          isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
        />
      )}
    </Stack>
  );
}
